import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createQuestion, getQuestionById, deleteQuestion, editQuestion } from '../../redux/apiRequest';
import { createAxios } from '../../createInstance';
import { loginSuccess } from '../../redux/authSlice';
import { useParams } from 'react-router-dom';
import { showToast } from "../../Components/ToastMessage";

import { LuDelete } from 'react-icons/lu';

import styles from './Subject.module.scss';
import classNames from 'classnames/bind';

const cx = classNames.bind(styles);

function Subject() {
    const { id } = useParams();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.login?.currentUser);
    const axiosJWT = useMemo(() => createAxios(user, dispatch, loginSuccess), [user, dispatch]);

    const questionsState = useSelector((state) => state.questions.questions);
    const listQuestions = Array.isArray(questionsState?.listquestion) ? questionsState.listquestion : [];
    const totalQuestions = listQuestions.length;

    const [questionText, setQuestionText] = useState('');
    const [image, setImage] = useState(null);
    const [option1, setOption1] = useState('');
    const [option2, setOption2] = useState('');
    const [option3, setOption3] = useState('');
    const [option4, setOption4] = useState('');
    const [correctOption, setCorrectOption] = useState('');
    const [errors, setErrors] = useState({});
    const [localEdits, setLocalEdits] = useState({});
    const [editingField, setEditingField] = useState(null);

    const fileInputRef = useRef(null);

    const validateForm = useCallback(() => {
        const validationErrors = {};
        if (!questionText.trim()) validationErrors.questionText = 'Vui lòng nhập nội dung câu hỏi.';
        if (!option1.trim()) validationErrors.option1 = 'Đáp án 1 là bắt buộc.';
        if (!option2.trim()) validationErrors.option2 = 'Đáp án 2 là bắt buộc.';
        if (!correctOption.trim()) validationErrors.correctOption = 'Hãy chỉ định đáp án đúng.';
        return validationErrors;
    }, [questionText, option1, option2, correctOption]);

    const resetForm = useCallback(() => {
        setQuestionText('');
        setImage(null);
        setOption1('');
        setOption2('');
        setOption3('');
        setOption4('');
        setCorrectOption('');
        setErrors({});
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    const successCallback = useCallback(async () => {
        if (!user?.accessToken) return;
        await getQuestionById(id, user.accessToken, dispatch, axiosJWT);
        showToast({
            type: "success",
            title: "Thành công!",
            message: "Xử lý câu hỏi thành công.",
            duration: 3000
        })
    }, [id, user?.accessToken, dispatch, axiosJWT]);

    const errCallback = useCallback(() => {
        showToast({
            type: "error",
            title: "Thất bại!",
            message: "Vui lòng thử lại sau.",
            duration: 3000
        })
    }, []);

    useEffect(() => {
        const validQuestionIds = new Set(listQuestions.map((question) => question._id));
        setLocalEdits((prev) => {
            let hasChanges = false;
            const next = {};
            Object.entries(prev).forEach(([questionId, edits]) => {
                if (validQuestionIds.has(questionId)) {
                    next[questionId] = edits;
                } else {
                    hasChanges = true;
                }
            });
            return hasChanges ? next : prev;
        });
        setEditingField((prev) => {
            if (prev && !validQuestionIds.has(prev.questionId)) {
                return null;
            }
            return prev;
        });
    }, [listQuestions]);

    const getDisplayValue = useCallback((questionId, field, fallbackValue = '') => {
        const questionEdits = localEdits[questionId];
        if (questionEdits && Object.prototype.hasOwnProperty.call(questionEdits, field)) {
            return questionEdits[field];
        }
        return fallbackValue ?? '';
    }, [localEdits]);

    const removeLocalEditField = useCallback((questionId, field) => {
        setLocalEdits((prev) => {
            const questionEdits = prev[questionId];
            if (!questionEdits || !Object.prototype.hasOwnProperty.call(questionEdits, field)) {
                return prev;
            }
            const { [field]: _removedField, ...remainingFields } = questionEdits;
            if (Object.keys(remainingFields).length === 0) {
                const { [questionId]: _removedQuestion, ...remainingQuestions } = prev;
                return remainingQuestions;
            }
            return {
                ...prev,
                [questionId]: remainingFields,
            };
        });
    }, []);

    const commitQuestionField = useCallback(
        async (question, field) => {
            if (!question?._id) {
                return;
            }
            const questionId = question._id;
            const editedValue = getDisplayValue(questionId, field, question[field] ?? '');
            const originalValue = question[field] ?? '';
            const normalizeValue = (value) => (typeof value === 'string' ? value.trim() : value);
            const normalizedEdited = normalizeValue(editedValue);
            const normalizedOriginal = normalizeValue(originalValue);

            if (normalizedEdited === normalizedOriginal) {
                removeLocalEditField(questionId, field);
                return;
            }

            if (!user?.accessToken) {
                showToast({
                    type: 'error',
                    title: 'Th?t b?i!',
                    message: 'Vui l�ng th? l?i sau.',
                    duration: 3000,
                });
                removeLocalEditField(questionId, field);
                return;
            }

            const formData = new FormData();
            if (typeof editedValue === 'string') {
                formData.append(field, normalizedEdited);
            } else if (editedValue !== undefined && editedValue !== null) {
                formData.append(field, editedValue);
            }

            await editQuestion(questionId, dispatch, user.accessToken, formData, axiosJWT, successCallback, errCallback);
            removeLocalEditField(questionId, field);
        },
        [axiosJWT, dispatch, editQuestion, errCallback, getDisplayValue, removeLocalEditField, successCallback, user?.accessToken]
    );

    const handleStartEditing = useCallback((questionId, field, initialValue = '') => {
        setLocalEdits((prev) => {
            const questionEdits = prev[questionId] ?? {};
            if (Object.prototype.hasOwnProperty.call(questionEdits, field)) {
                return prev;
            }
            return {
                ...prev,
                [questionId]: {
                    ...questionEdits,
                    [field]: initialValue ?? '',
                },
            };
        });
        setEditingField({ questionId, field });
    }, []);

    const handleEditingChange = useCallback((questionId, field, value) => {
        setLocalEdits((prev) => ({
            ...prev,
            [questionId]: {
                ...(prev[questionId] ?? {}),
                [field]: value,
            },
        }));
    }, []);

    const handleStopEditing = useCallback(
        (question, field, shouldCommit = true) => {
            setEditingField(null);
            if (!question || !field) {
                return;
            }
            if (shouldCommit) {
                commitQuestionField(question, field);
            } else {
                removeLocalEditField(question._id, field);
            }
        },
        [commitQuestionField, removeLocalEditField]
    );

    const handleEditingKeyDown = useCallback(
        (event, question, field) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                handleStopEditing(question, field, true);
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                handleStopEditing(question, field, false);
            }
        },
        [handleStopEditing]
    );
    const handleSubmit = useCallback(async (event) => {
        event?.preventDefault();
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        if (!user?.accessToken) {
            showToast({
                type: "error",
                title: "Thất bại!",
                message: "Vui lòng thử lại sau.",
                duration: 3000
            })
            return;
        }

        const formData = new FormData();
        formData.append('subject_id', id);
        formData.append('question_text', questionText.trim());
        formData.append('option1', option1.trim());
        formData.append('option2', option2.trim());
        formData.append('option3', option3.trim());
        formData.append('option4', option4.trim());
        formData.append('correct_option', correctOption.trim());
        if (image) {
            formData.append('image', image);
        }

        const successCallbackCreate = async () => {
            resetForm();
            await successCallback();
        };

        await createQuestion(dispatch, user.accessToken, formData, axiosJWT, successCallbackCreate, errCallback);
    }, [dispatch, user?.accessToken, id, questionText, option1, option2, option3, option4, correctOption, image, axiosJWT, validateForm, resetForm, successCallback, errCallback]);

    const handleDeleteQuestion = useCallback((questionId) => {
        if (!user?.accessToken) {
            showToast({
                type: "error",
                title: "Thất bại!",
                message: "Vui lòng thử lại sau.",
                duration: 3000
            })
            return;
        }
        deleteQuestion(questionId, dispatch, user.accessToken, axiosJWT, successCallback, errCallback);
    }, [dispatch, user?.accessToken, axiosJWT, successCallback, errCallback]);


    useEffect(() => {
        if (user?.accessToken) {
            getQuestionById(id, user.accessToken, dispatch, axiosJWT);
        }
    }, [id, user?.accessToken, dispatch, axiosJWT]);

    const optionInputs = [
        { id: 'option1', label: 'Đáp án 1', placeholder: 'Nhập đáp án 1', value: option1, onChange: setOption1, error: errors.option1 },
        { id: 'option2', label: 'Đáp án 2', placeholder: 'Nhập đáp án 2', value: option2, onChange: setOption2, error: errors.option2 },
        { id: 'option3', label: 'Đáp án 3', placeholder: 'Nhập đáp án 3 (không bắt buộc)', value: option3, onChange: setOption3 },
        { id: 'option4', label: 'Đáp án 4', placeholder: 'Nhập đáp án 4 (không bắt buộc)', value: option4, onChange: setOption4 },
    ];

    return (
        <section className={cx('subject-page')}>

            <div className={cx('subject-page__content')}>
                <form className={cx('composer-card')} onSubmit={handleSubmit}>
                    <div className={cx('composer-card__header')}>
                        <h2>Thêm câu hỏi</h2>
                        <p>Nhập nội dung, ảnh minh họa (nếu có) và các lựa chọn trả lời.</p>
                    </div>

                    <div className={cx('field')}>
                        <label htmlFor="question-text">Nội dung câu hỏi</label>
                        <textarea
                            id="question-text"
                            className={cx('textarea')}
                            placeholder="Nhập nội dung câu hỏi"
                            value={questionText}
                            onChange={(e) => setQuestionText(e.target.value)}
                        />
                        {errors.questionText && <span className={cx('field-error')}>{errors.questionText}</span>}
                    </div>

                    <div className={cx('field')}>
                        <label htmlFor="question-image">Ảnh minh họa</label>
                        <input
                            id="question-image"
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className={cx('file-input')}
                            onChange={(e) => setImage(e.target.files?.[0] || null)}
                        />
                    </div>

                    <div className={cx('field-grid')}>
                        {optionInputs.map(({ id: optionId, label, placeholder, value, onChange, error }) => (
                            <div key={optionId} className={cx('field')}>
                                <label htmlFor={optionId}>{label}</label>
                                <input
                                    id={optionId}
                                    type="text"
                                    placeholder={placeholder}
                                    value={value}
                                    onChange={(e) => onChange(e.target.value)}
                                />
                                {error && <span className={cx('field-error')}>{error}</span>}
                            </div>
                        ))}
                    </div>

                    <div className={cx('field')}>
                        <label htmlFor="correct-option">Đáp án đúng</label>
                        <input
                            id="correct-option"
                            type="text"
                            placeholder="Nhập số thứ tự của đáp án đúng (1-4)"
                            value={correctOption}
                            onChange={(e) => setCorrectOption(e.target.value)}
                        />
                        {errors.correctOption && <span className={cx('field-error')}>{errors.correctOption}</span>}
                    </div>

                    <div className={cx('composer-card__actions')}>
                        <button type="button" className={cx('secondary-button')} onClick={resetForm}>
                            Làm mới
                        </button>
                        <button type="submit" className={cx('primary-button')}>
                            Lưu câu hỏi
                        </button>
                    </div>
                </form>

                <div className={cx('questions-panel')}>
                    <div className={cx('questions-panel__header')}>
                        <div>
                            <h2>Danh sách câu hỏi</h2>
                            <p>{totalQuestions} câu hỏi</p>
                        </div>
                    </div>

                    <div className={cx('questions-panel__body')}>
                        {totalQuestions === 0 ? (
                            <div className={cx('empty-state')}>
                                <h3>Chưa có câu hỏi nào</h3>
                                <p>Hãy bắt đầu tạo câu hỏi đầu tiên ở khung bên trái.</p>
                            </div>
                        ) : (
                            listQuestions.map((question, index) => {
                                const questionTextValue = getDisplayValue(question._id, 'question_text', question.question_text ?? '');
                                const isEditingQuestionText = editingField?.questionId === question._id && editingField.field === 'question_text';
                                const optionFieldConfigs = [
                                    { id: 'A', field: 'option1' },
                                    { id: 'B', field: 'option2' },
                                    { id: 'C', field: 'option3' },
                                    { id: 'D', field: 'option4' },
                                ];
                                const options = optionFieldConfigs
                                    .map((config, optionIndex) => {
                                        const optionText = getDisplayValue(question._id, config.field, question[config.field] ?? '');
                                        const isEditingOption = editingField?.questionId === question._id && editingField.field === config.field;
                                        return {
                                            id: config.id,
                                            field: config.field,
                                            text: optionText,
                                            isCorrect: question.correct_option === optionIndex + 1,
                                            isEditing: isEditingOption,
                                        };
                                    })
                                    .filter(({ text, isEditing }) => Boolean(text) || isEditing);

                                return (
                                    <article key={question._id} className={cx('question-card')}>
                                        <div className={cx('question-card__top')}>
                                            <span className={cx('question-card__index')}>{index + 1}</span>
                                            <button
                                                type="button"
                                                className={cx('icon-button')}
                                                onClick={() => handleDeleteQuestion(question._id)}
                                                aria-label="Xoa cau hoi"
                                            >
                                                <LuDelete />
                                            </button>
                                        </div>

                                        <p
                                            className={cx('question-card__text', { 'question-card__text--editing': isEditingQuestionText })}
                                            onDoubleClick={() => handleStartEditing(question._id, 'question_text', questionTextValue)}
                                        >
                                            {isEditingQuestionText ? (
                                                <input
                                                    autoFocus
                                                    className={cx('question-card__text-input')}
                                                    value={questionTextValue}
                                                    onChange={(event) => handleEditingChange(question._id, 'question_text', event.target.value)}
                                                    onBlur={() => handleStopEditing(question, 'question_text')}
                                                    onKeyDown={(event) => handleEditingKeyDown(event, question, 'question_text')}
                                                />
                                            ) : (
                                                questionTextValue
                                            )}
                                        </p>

                                        {question.image_url && (
                                            <img src={question.image_url} alt="Question" className={cx('question-card__image')} />
                                        )}

                                        <ul className={cx('question-card__options')}>
                                            {options.map((option) => (
                                                <li
                                                    key={option.id}
                                                    className={cx('question-card__option', {
                                                        'question-card__option--correct': option.isCorrect,
                                                    })}
                                                    onDoubleClick={() => handleStartEditing(question._id, option.field, option.text)}
                                                >
                                                    <span className={cx('option-badge')}>{option.id}</span>
                                                    {option.isEditing ? (
                                                        <input
                                                            autoFocus
                                                            className={cx('question-card__option-input')}
                                                            value={option.text}
                                                            onChange={(event) => handleEditingChange(question._id, option.field, event.target.value)}
                                                            onBlur={() => handleStopEditing(question, option.field)}
                                                            onKeyDown={(event) => handleEditingKeyDown(event, question, option.field)}
                                                        />
                                                    ) : (
                                                        <span className={cx('question-card__option-text')}>{option.text}</span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </article>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default Subject;
